import React, { useEffect, useState, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import { queryStringToObject } from 'client/helpers/queryStringToObject';
import UserMenu from './UserMenu';

/* ------------ helpers sûrs pour SSR ------------ */
const safeGet = (key: string) => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, value);
  } catch {}
};
const safeRemove = (key: string) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {}
};
const safeGetToken = () => safeGet('token_b2b');
const safeGetUser = () => {
  const raw = safeGet('userProfil');
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
const safeSetUser = (obj: any) => safeSet('userProfil', JSON.stringify(obj));
const safeClearSession = () => {
  safeRemove('token_b2b');
  safeRemove('userProfil');
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.clear();
    } catch {}
  }
};

interface IProps {
  navList: IData[];
}
interface IData {
  title: string;
  link: string;
  list?: IData[];
}

const Navbar: React.FC<IProps> = ({ navList }) => {
  const location = useLocation();
  const history = useHistory();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);

  /* ---- 1) Hydrate depuis storage au montage ---- */
  useEffect(() => {
    setIsLoggedIn(Boolean(safeGetToken()));
    setUser(safeGetUser());

    const onStorage = () => {
      setIsLoggedIn(Boolean(safeGetToken()));
      setUser(safeGetUser());
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
  }, []);

  /* ---- 2) Recalcule après navigation (ex: retour du login) ---- */
  useEffect(() => {
    setIsLoggedIn(Boolean(safeGetToken()));
    setUser(safeGetUser());
  }, [location.pathname, location.search]);

  /* ---- 3) Optionnel : si token existe mais pas de userProfil, on le charge ---- */
  useEffect(() => {
    const token = safeGetToken();
    if (!token || user) return;

    fetch('/api/me', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          safeSetUser(data);
          setUser(data);
        }
      })
      .catch(() => {
        /* silencieux */
      });
  }, [user]);

  const getIsCurrentLink = useCallback(
    (str: string) =>
      str === `${location.pathname}${location.search || ''}` ||
      (location.search &&
        queryStringToObject(location.search)['product-type'] &&
        queryStringToObject(str)['product-type'] &&
        queryStringToObject(location.search)['product-type'] ===
          queryStringToObject(str)['product-type']),
    [location.pathname, location.search],
  );

  const handleMyReservation = () => {
    if (safeGetToken()) history.push('/b2b/mes-reservations');
    else history.push('/checking-reservation-details');
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    setLogoutError(null);

    try {
      await fetch('/api/b2b/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      // La route peut ne pas exister selon l'env → on déconnecte localement.
      setLogoutError('Déconnexion locale effectuée.');
    } finally {
      safeClearSession();
      setIsLoggedIn(false);
      setUser(null);
      setIsLoggingOut(false);
      history.replace('/b2b-login');
    }
  };

  const espaceB2bHref = isLoggedIn ? '/b2b/catalogue-b2b' : '/espace-b2b';

  // ✅ Nouveau: build href selon mode B2B/B2C
  const buildNavHref = useCallback((title: string, defaultLink: string) => {
    const isB2B = Boolean(safeGetToken());
    const t = title.toLowerCase();

    // ✅ B2B
    if (isB2B) {
      if (t.includes('séjour') || t.includes('sejour')) {
        return '/b2b/catalogue-b2b?tab=sejour#filters';
      }
      if (t.includes('circuit')) {
        return '/b2b/catalogue-b2b?tab=tour#filters';
      }
      return defaultLink;
    }

    // ✅ B2C
    if (t.includes('séjour') || t.includes('sejour')) {
      return '/products?product-type=sejour&sort=asc';
    }

    if (t.includes('circuit')) {
      return '/products?product-type=tour&sort=asc';
    }

    return defaultLink;
  }, []);

  // ✅ Nouveau: handle click (pour forcer history.push et éviter navigation "bizarre")
  const handleNavClick = useCallback(
    (ev: React.MouseEvent, href: string) => {
      ev.preventDefault();
      history.push(href);
    },
    [history],
  );

  return (
    <nav className="navbar navbar-expand-lg text-primary align-items-center justify-content-start bg-white">
      <ul style={{ listStyle: 'none' }} className="d-flex align-content-center mb-0 pl-0">
        {navList.map((e) => {
          const computedHref = buildNavHref(e.title, e.link);

          const linkEl = (
            <Link to={computedHref} onClick={(ev) => handleNavClick(ev, computedHref)}>
              {e.title}
            </Link>
          );

          return (
            <li
              key={e.title}
              title={e.title.toLowerCase()}
              className={`h4 mx-2 my-auto ${
                e.list ? 'position-relative dropdown-toggle dropdown-title' : ''
              }`}
            >
              {getIsCurrentLink(computedHref) ? (
                <u>
                  {linkEl}
                  {e.list && (
                    <ul className="h4 dropdown-menu px-2 rounded">
                      {e.list.map((el) => (
                        <Link key={el.title} to={el.link}>
                          <span className="py-2 text-underline">{el.title}</span>
                        </Link>
                      ))}
                    </ul>
                  )}
                </u>
              ) : (
                <>
                  {linkEl}
                  {e.list && (
                    <ul className="h4 dropdown-menu px-2 rounded">
                      {e.list.map((el) => (
                        <Link key={el.title} to={el.link}>
                          <span className="py-2 text-underline">{el.title}</span>
                        </Link>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </li>
          );
        })}

        {/* Espace B2B */}
        <li title="Espace B2B" className="h4 mx-2 my-auto">
          <Link to={espaceB2bHref}>
            <button className="btn btn-primary border-0" type="button">
              Espace B2B
            </button>
          </Link>
        </li>

        {/* Mes réservations */}
        <li title="Mes réservations" className="h4 mx-2 my-auto">
          <button className="btn btn-primary border-0" onClick={handleMyReservation}>
            Ma réservation
          </button>
        </li>

        {/* Menu utilisateur — uniquement si connecté */}
        {isLoggedIn && <UserMenu user={user || {}} onLogout={handleLogout} />}

        {/* Si non connecté */}
        {!isLoggedIn && (
          <li className="h4 mx-2 my-auto">
            <Link to="/b2b-login">
              <button
                className="btn btn-outline-primary border-1 "
                style={{ borderRadius: '25px' }}
              >
                <span className="fa fa-user mr-1"></span>
                Se connecter
              </button>
            </Link>
          </li>
        )}

        {/* Message d'erreur logout éventuel */}
        {logoutError && <small className="text-danger ml-2">{logoutError}</small>}
      </ul>
    </nav>
  );
};

export default Navbar;
